import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { macSpring } from '../../utils/macAnimations';
import { 
  Maximize2, 
  RotateCw, 
  Sliders, 
  AlertTriangle, 
  CheckCircle2, 
  Scissors, 
  ShieldCheck, 
  Sparkles, 
  Move, 
  Layers, 
  Eye, 
  Info,
  ChevronDown,
  ChevronUp,
  Plus,
  Minus
} from 'lucide-react';
import { useSaaSContext } from '../../store/SaaSContext';
import { formatUnit, parseUnit } from '../../utils/units';
import { Unit } from '../../types';

interface ImpositionThumbnailPreviewProps {
  compact?: boolean;
  onOpenSettings?: () => void;
  className?: string;
}

export const ImpositionThumbnailPreview: React.FC<ImpositionThumbnailPreviewProps> = ({
  compact = false,
  onOpenSettings,
  className = ''
}) => {
  const { layout, setLayout, template, appUnit = 'mm' } = useSaaSContext();

  const [hoveredSlot, setHoveredSlot] = useState<{ row: number; col: number; index: number } | null>(null);
  const [activeTab, setActiveTab] = useState<'preview' | 'controls' | 'metrics'>('preview');
  const [showBleedOverlay, setShowBleedOverlay] = useState(true);
  const [showMarginOverlay, setShowMarginOverlay] = useState(true);
  const [showGapOverlay, setShowGapOverlay] = useState(true);
  const [showSafeZoneOverlay, setShowSafeZoneOverlay] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);

  // Safe numeric fallbacks
  const cols = Math.max(1, Number(layout.cols) || 1);
  const rows = Math.max(1, Number(layout.rows) || 1);
  const gapX = Math.max(0, Number(layout.gapX) || 0);
  const gapY = Math.max(0, Number(layout.gapY) || 0);
  const marginX = Math.max(0, Number(layout.marginX) || 0);
  const marginY = Math.max(0, Number(layout.marginY) || 0);
  const bleed = Math.max(0, Number(layout.bleed) || 0);
  const safeZone = Math.max(0, Number(layout.safeZone) || 0);

  // Calculate Sheet Dimensions
  const rawSheetWidth = layout.pageSize === 'custom' && layout.pageWidth 
    ? Number(layout.pageWidth) 
    : (layout.pageSize === 'A3' ? 842 : 595);
  const rawSheetHeight = layout.pageSize === 'custom' && layout.pageHeight 
    ? Number(layout.pageHeight) 
    : (layout.pageSize === 'A3' ? 1190 : 842);
  const isLandscape = layout.pageOrientation === 'landscape';
  const sheetWidth = isLandscape ? rawSheetHeight : rawSheetWidth;
  const sheetHeight = isLandscape ? rawSheetWidth : rawSheetHeight;

  // Raw Sticker / Item Dimensions
  const rawItemW = Number(template.width) || 150;
  const rawItemH = Number(template.height) || 150;
  const itemW = layout.rotateStickers ? rawItemH : rawItemW;
  const itemH = layout.rotateStickers ? rawItemW : rawItemH;

  // Item with Bleed Dimensions
  const itemWithBleedW = itemW + (bleed * 2);
  const itemWithBleedH = itemH + (bleed * 2);

  // Total Imposition Footprint
  const totalGridW = (itemWithBleedW * cols) + (gapX * (cols - 1));
  const totalGridH = (itemWithBleedH * rows) + (gapY * (rows - 1));

  const totalOccupiedW = marginX * 2 + totalGridW;
  const totalOccupiedH = marginY * 2 + totalGridH;

  // Overflow checks
  const isOverflowX = totalOccupiedW > sheetWidth + 0.5;
  const isOverflowY = totalOccupiedH > sheetHeight + 0.5;
  const isOverflow = isOverflowX || isOverflowY;

  const overflowAmountX = Math.max(0, totalOccupiedW - sheetWidth);
  const overflowAmountY = Math.max(0, totalOccupiedH - sheetHeight);

  // Paper Efficiency
  const printableArea = Math.max(0, (sheetWidth - marginX * 2)) * Math.max(0, (sheetHeight - marginY * 2));
  const totalStickerArea = (itemW * itemH) * (cols * rows);
  const sheetArea = sheetWidth * sheetHeight;
  const efficiency = sheetArea > 0 ? Math.min(100, Math.round((totalStickerArea / sheetArea) * 100)) : 0;

  // Disabled Slots set
  const disabledSlotsSet = useMemo(() => {
    return new Set(layout.disabledSlots || []);
  }, [layout.disabledSlots]);

  // Handle cell click to toggle disable
  const handleToggleSlot = (row: number, col: number) => {
    const slotKey = `${row}-${col}`;
    const current = layout.disabledSlots || [];
    const exists = current.includes(slotKey);
    const updated = exists ? current.filter(k => k !== slotKey) : [...current, slotKey];
    setLayout(prev => ({ ...prev, disabledSlots: updated }));
  };

  // Quick adjustment handlers
  const handleUpdate = (key: keyof typeof layout, value: any) => {
    setLayout(prev => ({ ...prev, [key]: value }));
  };

  const handleNudge = (key: 'gapX' | 'gapY' | 'marginX' | 'marginY' | 'bleed', deltaMm: number) => {
    const stepInPt = parseUnit(Math.abs(deltaMm), appUnit) * (deltaMm < 0 ? -1 : 1);
    const current = Number(layout[key]) || 0;
    const next = Math.max(0, current + stepInPt);
    handleUpdate(key, next);
  };

  const handleCenterGrid = () => {
    const newMarginX = Math.max(0, (sheetWidth - totalGridW) / 2);
    const newMarginY = Math.max(0, (sheetHeight - totalGridH) / 2);
    setLayout(prev => ({ ...prev, marginX: newMarginX, marginY: newMarginY }));
  };

  const handleAutoFitOptimal = () => {
    const availW = sheetWidth - (marginX * 2);
    const availH = sheetHeight - (marginY * 2);

    const calcCols = Math.max(1, Math.floor((availW + gapX) / (itemWithBleedW + gapX)));
    const calcRows = Math.max(1, Math.floor((availH + gapY) / (itemWithBleedH + gapY)));

    setLayout(prev => ({ ...prev, cols: calcCols, rows: calcRows }));
  };

  // SVG ViewBox scaling
  const paddingSvg = 24;
  const viewBoxW = sheetWidth + paddingSvg * 2;
  const viewBoxH = sheetHeight + paddingSvg * 2;

  // Unit step standard (1 unit in current appUnit converted to internal pt)
  const unitStep = parseUnit(1, appUnit);

  return (
    <div className={`bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden shadow-xl flex flex-col ${className}`}>
      {/* Header Bar */}
      <div className="px-3 py-2.5 bg-zinc-900 border-b border-zinc-800/80 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center shrink-0">
            <Layers className="w-3.5 h-3.5 text-blue-400" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-bold text-zinc-100 truncate">Imposition Grid Thumbnail</span>
              <span className={`text-[10px] px-1.5 py-0.2 rounded font-mono font-semibold ${
                isOverflow 
                  ? 'bg-rose-950/80 text-rose-300 border border-rose-800/60 animate-pulse' 
                  : 'bg-emerald-950/80 text-emerald-300 border border-emerald-800/60'
              }`}>
                {isOverflow ? 'Overflow Warning' : `${cols * rows} Up (${efficiency}%)`}
              </span>
            </div>
            <p className="text-[10px] text-zinc-400 font-mono truncate">
              {formatUnit(sheetWidth, appUnit)}×{formatUnit(sheetHeight, appUnit)} {appUnit} • {layout.pageSize || 'A4'} ({isLandscape ? 'Landscape' : 'Portrait'})
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <div className="flex bg-zinc-950 p-0.5 rounded-lg border border-zinc-800 text-[10px]">
            <button
              onClick={() => setActiveTab('preview')}
              className={`px-2 py-0.5 rounded font-medium transition-colors ${
                activeTab === 'preview' ? 'bg-blue-600 text-white shadow-xs' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Grid
            </button>
            <button
              onClick={() => setActiveTab('controls')}
              className={`px-2 py-0.5 rounded font-medium transition-colors ${
                activeTab === 'controls' ? 'bg-blue-600 text-white shadow-xs' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Quick Tweak
            </button>
            <button
              onClick={() => setActiveTab('metrics')}
              className={`px-2 py-0.5 rounded font-medium transition-colors ${
                activeTab === 'metrics' ? 'bg-blue-600 text-white shadow-xs' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Stats
            </button>
          </div>

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 rounded transition-colors"
            title={isExpanded ? "Collapse Thumbnail" : "Expand Thumbnail"}
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={macSpring}
            className="p-3 space-y-2.5 overflow-hidden"
          >
            {/* Overflow Alert Banner if needed */}
            {isOverflow && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-2 bg-rose-950/70 border border-rose-800/80 rounded-lg flex items-center justify-between gap-2 text-rose-200 text-xs shadow-inner"
              >
              <div className="flex items-center gap-2 min-w-0">
                <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
                <span className="truncate text-[11px]">
                  Grid exceeds sheet by {isOverflowX ? `W: +${formatUnit(overflowAmountX, appUnit)} ` : ''}{isOverflowY ? `H: +${formatUnit(overflowAmountY, appUnit)} ` : ''}{appUnit}
                </span>
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={handleAutoFitOptimal}
                  className="px-2 py-0.5 bg-rose-800 hover:bg-rose-700 text-white rounded text-[10px] font-semibold cursor-pointer"
                >
                  Auto-Fit
                </button>
                <button
                  onClick={handleCenterGrid}
                  className="px-2 py-0.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded text-[10px] cursor-pointer"
                >
                  Center
                </button>
              </div>
            </motion.div>
          )}

          {/* TAB 1: Real-time SVG Canvas Preview */}
          {activeTab === 'preview' && (
            <motion.div 
              key="preview"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={macSpring}
              className="space-y-2"
            >
              {/* Layer Visibility Pills */}
              <div className="flex items-center justify-between gap-1 overflow-x-auto text-[10px] pb-1 border-b border-zinc-800/60 custom-scrollbar">
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => setShowMarginOverlay(!showMarginOverlay)}
                    className={`px-2 py-0.5 rounded border transition-colors flex items-center gap-1 ${
                      showMarginOverlay 
                        ? 'bg-purple-950/60 text-purple-300 border-purple-800/60' 
                        : 'bg-zinc-900 text-zinc-500 border-zinc-800'
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400 inline-block"></span>
                    Margin ({formatUnit(marginX, appUnit)}×{formatUnit(marginY, appUnit)})
                  </button>
                  <button
                    onClick={() => setShowGapOverlay(!showGapOverlay)}
                    className={`px-2 py-0.5 rounded border transition-colors flex items-center gap-1 ${
                      showGapOverlay 
                        ? 'bg-blue-950/60 text-blue-300 border-blue-800/60' 
                        : 'bg-zinc-900 text-zinc-500 border-zinc-800'
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block"></span>
                    Spacing ({formatUnit(gapX, appUnit)}×{formatUnit(gapY, appUnit)})
                  </button>
                  <button
                    onClick={() => setShowBleedOverlay(!showBleedOverlay)}
                    className={`px-2 py-0.5 rounded border transition-colors flex items-center gap-1 ${
                      showBleedOverlay 
                        ? 'bg-amber-950/60 text-amber-300 border-amber-800/60' 
                        : 'bg-zinc-900 text-zinc-500 border-zinc-800'
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block"></span>
                    Bleed ({formatUnit(bleed, appUnit)})
                  </button>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => setShowLabels(!showLabels)}
                    className={`px-1.5 py-0.5 rounded text-[10px] ${
                      showLabels ? 'text-zinc-300 bg-zinc-800' : 'text-zinc-500 bg-zinc-900'
                    }`}
                    title="Toggle Cell Labels"
                  >
                    #
                  </button>
                  <button
                    onClick={() => handleUpdate('rotateStickers', !layout.rotateStickers)}
                    className={`px-1.5 py-0.5 rounded text-[10px] flex items-center gap-0.5 border ${
                      layout.rotateStickers 
                        ? 'bg-indigo-950 text-indigo-300 border-indigo-700' 
                        : 'bg-zinc-900 text-zinc-400 border-zinc-800'
                    }`}
                    title="Rotate Stickers 90°"
                  >
                    <RotateCw className="w-2.5 h-2.5" /> 90°
                  </button>
                </div>
              </div>

              {/* Interactive SVG Stage with Dotted Grid Background */}
              <div 
                className="relative w-full aspect-[4/3] rounded-lg border border-zinc-700/60 p-2 flex items-center justify-center overflow-hidden group shadow-inner"
                style={{
                  backgroundColor: '#f1f5f9',
                  backgroundImage: 'radial-gradient(circle, rgba(15, 23, 42, 0.4) 1px, transparent 1px)',
                  backgroundSize: '14px 14px'
                }}
              >
                <svg
                  viewBox={`${-paddingSvg} ${-paddingSvg} ${viewBoxW} ${viewBoxH}`}
                  className="w-full h-full max-h-[220px] filter drop-shadow-md select-none"
                  preserveAspectRatio="xMidYMid meet"
                >
                  <defs>
                    {/* Diagonal hatch pattern for margins */}
                    <pattern id="margin-hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                      <line x1="0" y1="0" x2="0" y2="8" stroke="#a855f7" strokeWidth="1" strokeOpacity="0.25" />
                    </pattern>
                    {/* Diagonal hatch pattern for gaps */}
                    <pattern id="gap-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
                      <line x1="0" y1="0" x2="0" y2="6" stroke="#3b82f6" strokeWidth="1" strokeOpacity="0.2" />
                    </pattern>
                    {/* Disabled slot pattern */}
                    <pattern id="disabled-slot-hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                      <line x1="0" y1="0" x2="0" y2="8" stroke="#ef4444" strokeWidth="1.5" strokeOpacity="0.4" />
                    </pattern>
                  </defs>

                  {/* 1. Base Paper Sheet */}
                  <rect
                    x="0"
                    y="0"
                    width={sheetWidth}
                    height={sheetHeight}
                    rx="4"
                    fill="#ffffff"
                    stroke={isOverflow ? '#ef4444' : '#64748b'}
                    strokeWidth={isOverflow ? '2' : '1'}
                  />

                  {/* 2. Margin Shaded Zone */}
                  {showMarginOverlay && (marginX > 0 || marginY > 0) && (
                    <g>
                      {/* Top margin */}
                      {marginY > 0 && (
                        <rect x="0" y="0" width={sheetWidth} height={marginY} fill="url(#margin-hatch)" />
                      )}
                      {/* Bottom margin */}
                      {marginY > 0 && (
                        <rect x="0" y={sheetHeight - marginY} width={sheetWidth} height={marginY} fill="url(#margin-hatch)" />
                      )}
                      {/* Left margin */}
                      {marginX > 0 && (
                        <rect x="0" y={marginY} width={marginX} height={Math.max(0, sheetHeight - marginY * 2)} fill="url(#margin-hatch)" />
                      )}
                      {/* Right margin */}
                      {marginX > 0 && (
                        <rect x={sheetWidth - marginX} y={marginY} width={marginX} height={Math.max(0, sheetHeight - marginY * 2)} fill="url(#margin-hatch)" />
                      )}
                      {/* Margin inner guideline */}
                      <rect
                        x={marginX}
                        y={marginY}
                        width={Math.max(0, sheetWidth - marginX * 2)}
                        height={Math.max(0, sheetHeight - marginY * 2)}
                        fill="none"
                        stroke="#a855f7"
                        strokeWidth="1"
                        strokeDasharray="4 3"
                        strokeOpacity="0.6"
                      />
                    </g>
                  )}

                  {/* 3. Grid Cells Render */}
                  {Array.from({ length: rows }).map((_, r) => {
                    return Array.from({ length: cols }).map((_, c) => {
                      const cellX = marginX + c * (itemWithBleedW + gapX);
                      const cellY = marginY + r * (itemWithBleedH + gapY);
                      const slotKey = `${r}-${c}`;
                      const isDisabled = disabledSlotsSet.has(slotKey);
                      const isHovered = hoveredSlot?.row === r && hoveredSlot?.col === c;
                      const slotIndex = r * cols + c + 1;

                      // Check if this specific cell overflows the sheet
                      const cellOverflows = (cellX + itemWithBleedW > sheetWidth + 0.1) || (cellY + itemWithBleedH > sheetHeight + 0.1);

                      return (
                        <g
                          key={slotKey}
                          className="cursor-pointer transition-transform"
                          onMouseEnter={() => setHoveredSlot({ row: r, col: c, index: slotIndex })}
                          onMouseLeave={() => setHoveredSlot(null)}
                          onClick={() => handleToggleSlot(r, c)}
                        >
                          {/* Bleed Area (if bleed > 0 and overlay active) */}
                          {showBleedOverlay && bleed > 0 && (
                            <rect
                              x={cellX}
                              y={cellY}
                              width={itemWithBleedW}
                              height={itemWithBleedH}
                              rx="2"
                              fill={cellOverflows ? '#fca5a5' : '#fef3c7'}
                              fillOpacity={cellOverflows ? 0.35 : 0.3}
                              stroke={cellOverflows ? '#ef4444' : '#f59e0b'}
                              strokeWidth="0.75"
                              strokeDasharray="2 2"
                            />
                          )}

                          {/* Trim Area (Cut Sticker) */}
                          <rect
                            x={cellX + bleed}
                            y={cellY + bleed}
                            width={itemW}
                            height={itemH}
                            rx={template.shape === 'rounded' ? 4 : (template.shape === 'circle' ? itemW / 2 : 1)}
                            fill={isDisabled ? '#fee2e2' : (isHovered ? '#dbeafe' : '#f8fafc')}
                            stroke={isDisabled ? '#ef4444' : (isHovered ? '#2563eb' : (cellOverflows ? '#dc2626' : '#94a3b8'))}
                            strokeWidth={isHovered ? '1.5' : '1'}
                          />

                          {/* Disabled Hatch Pattern */}
                          {isDisabled && (
                            <rect
                              x={cellX + bleed}
                              y={cellY + bleed}
                              width={itemW}
                              height={itemH}
                              fill="url(#disabled-slot-hatch)"
                            />
                          )}

                          {/* Slot Center Label */}
                          {showLabels && (
                            <text
                              x={cellX + bleed + itemW / 2}
                              y={cellY + bleed + itemH / 2}
                              textAnchor="middle"
                              dominantBaseline="central"
                              fontSize={Math.min(11, Math.max(7, itemW / 4.5))}
                              fill={isDisabled ? '#dc2626' : (isHovered ? '#1d4ed8' : '#64748b')}
                              fontWeight="600"
                              fontFamily="monospace"
                            >
                              {isDisabled ? '✕' : `#${slotIndex}`}
                            </text>
                          )}
                        </g>
                      );
                    });
                  })}

                  {/* 4. Sheet Dimension Ticks */}
                  <text
                    x={sheetWidth / 2}
                    y={-6}
                    textAnchor="middle"
                    fontSize="9"
                    fill="#94a3b8"
                    fontFamily="monospace"
                  >
                    {formatUnit(sheetWidth, appUnit)} {appUnit}
                  </text>
                  <text
                    x={-6}
                    y={sheetHeight / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize="9"
                    fill="#94a3b8"
                    fontFamily="monospace"
                    transform={`rotate(-90, -6, ${sheetHeight / 2})`}
                  >
                    {formatUnit(sheetHeight, appUnit)} {appUnit}
                  </text>
                </svg>

                {/* Floating Tooltip upon slot hover */}
                {hoveredSlot && (
                  <div className="absolute bottom-2 left-2 right-2 bg-zinc-950/95 border border-zinc-700/80 rounded-lg p-1.5 px-2.5 text-[10px] text-zinc-200 flex items-center justify-between shadow-xl backdrop-blur-sm pointer-events-none animate-in fade-in duration-100">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-blue-400 font-mono">Slot #{hoveredSlot.index}</span>
                      <span className="text-zinc-400">Row {hoveredSlot.row + 1}, Col {hoveredSlot.col + 1}</span>
                      <span className="text-zinc-600">•</span>
                      <span>{formatUnit(itemW, appUnit)}×{formatUnit(itemH, appUnit)} {appUnit}</span>
                      {bleed > 0 && <span className="text-amber-400 font-mono">+{formatUnit(bleed, appUnit)} bld</span>}
                    </div>
                    <span className="text-[9px] text-zinc-400 font-sans">Click to {disabledSlotsSet.has(`${hoveredSlot.row}-${hoveredSlot.col}`) ? 'Enable' : 'Disable'}</span>
                  </div>
                )}
              </div>

              {/* Bottom Quick Controls Toolbar */}
              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={handleCenterGrid}
                    className="px-2 py-1 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-700/60 rounded text-[10px] font-medium transition-colors flex items-center gap-1 cursor-pointer"
                    title="Center the imposition grid on sheet"
                  >
                    <Move className="w-3 h-3 text-blue-400" /> Center
                  </button>
                  <button
                    onClick={handleAutoFitOptimal}
                    className="px-2 py-1 bg-blue-950/70 hover:bg-blue-900 text-blue-300 border border-blue-800/60 rounded text-[10px] font-medium transition-colors flex items-center gap-1 cursor-pointer"
                    title="Auto-Fit optimal grid based on printable area"
                  >
                    <Sparkles className="w-3 h-3 text-blue-400" /> Auto-Fit
                  </button>
                </div>

                <div className="text-[10px] font-mono text-zinc-400">
                  {cols} × {rows} = <span className="text-zinc-100 font-bold">{cols * rows - disabledSlotsSet.size} Active</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* TAB 2: Interactive Quick Tweak Sliders & Steppers */}
          {activeTab === 'controls' && (
            <motion.div 
              key="controls"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={macSpring}
              className="space-y-3"
            >
              {/* Spacing / Gaps */}
              <div className="p-2.5 bg-zinc-900/90 border border-zinc-800 rounded-lg space-y-1.5">
                <div className="flex items-center justify-between text-xs font-semibold text-zinc-200">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    Spacing / Gaps ({appUnit})
                  </span>
                  <span className="font-mono text-blue-400 text-[11px]">
                    X: {formatUnit(gapX, appUnit)} | Y: {formatUnit(gapY, appUnit)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div>
                    <span className="text-[10px] text-zinc-400 mb-1 block">Gap X (Cols)</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleNudge('gapX', -1)}
                        className="p-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded text-xs cursor-pointer"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <input
                        type="number"
                        min="0"
                        className="w-full px-1.5 py-0.5 bg-zinc-950 border border-zinc-700 text-zinc-100 text-center font-mono text-xs rounded"
                        value={formatUnit(gapX, appUnit)}
                        onChange={(e) => handleUpdate('gapX', parseUnit(e.target.value, appUnit))}
                      />
                      <button
                        onClick={() => handleNudge('gapX', 1)}
                        className="p-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded text-xs cursor-pointer"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] text-zinc-400 mb-1 block">Gap Y (Rows)</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleNudge('gapY', -1)}
                        className="p-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded text-xs cursor-pointer"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <input
                        type="number"
                        min="0"
                        className="w-full px-1.5 py-0.5 bg-zinc-950 border border-zinc-700 text-zinc-100 text-center font-mono text-xs rounded"
                        value={formatUnit(gapY, appUnit)}
                        onChange={(e) => handleUpdate('gapY', parseUnit(e.target.value, appUnit))}
                      />
                      <button
                        onClick={() => handleNudge('gapY', 1)}
                        className="p-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded text-xs cursor-pointer"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 pt-1 overflow-x-auto">
                  <span className="text-[10px] text-zinc-500 self-center">Presets:</span>
                  {[0, 1, 2, 3, 5].map(val => (
                    <button
                      key={val}
                      onClick={() => {
                        const pt = parseUnit(val, appUnit);
                        setLayout(prev => ({ ...prev, gapX: pt, gapY: pt }));
                      }}
                      className="px-2 py-0.5 bg-zinc-950 hover:bg-zinc-800 text-[10px] text-zinc-300 rounded border border-zinc-800 font-mono"
                    >
                      {val}{appUnit}
                    </button>
                  ))}
                </div>
              </div>

              {/* Margins */}
              <div className="p-2.5 bg-zinc-900/90 border border-zinc-800 rounded-lg space-y-1.5">
                <div className="flex items-center justify-between text-xs font-semibold text-zinc-200">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                    Sheet Margins ({appUnit})
                  </span>
                  <span className="font-mono text-purple-400 text-[11px]">
                    X: {formatUnit(marginX, appUnit)} | Y: {formatUnit(marginY, appUnit)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div>
                    <span className="text-[10px] text-zinc-400 mb-1 block">Margin X</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleNudge('marginX', -1)}
                        className="p-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded text-xs cursor-pointer"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <input
                        type="number"
                        min="0"
                        className="w-full px-1.5 py-0.5 bg-zinc-950 border border-zinc-700 text-zinc-100 text-center font-mono text-xs rounded"
                        value={formatUnit(marginX, appUnit)}
                        onChange={(e) => handleUpdate('marginX', parseUnit(e.target.value, appUnit))}
                      />
                      <button
                        onClick={() => handleNudge('marginX', 1)}
                        className="p-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded text-xs cursor-pointer"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] text-zinc-400 mb-1 block">Margin Y</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleNudge('marginY', -1)}
                        className="p-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded text-xs cursor-pointer"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <input
                        type="number"
                        min="0"
                        className="w-full px-1.5 py-0.5 bg-zinc-950 border border-zinc-700 text-zinc-100 text-center font-mono text-xs rounded"
                        value={formatUnit(marginY, appUnit)}
                        onChange={(e) => handleUpdate('marginY', parseUnit(e.target.value, appUnit))}
                      />
                      <button
                        onClick={() => handleNudge('marginY', 1)}
                        className="p-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded text-xs cursor-pointer"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bleed & Safe Zone */}
              <div className="p-2.5 bg-zinc-900/90 border border-zinc-800 rounded-lg space-y-1.5">
                <div className="flex items-center justify-between text-xs font-semibold text-zinc-200">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                    Bleed &amp; Safe Zone ({appUnit})
                  </span>
                  <span className="font-mono text-amber-400 text-[11px]">
                    Bleed: {formatUnit(bleed, appUnit)}
                  </span>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={() => handleNudge('bleed', -0.5)}
                    className="p-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded text-xs cursor-pointer"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    className="flex-1 px-2 py-1 bg-zinc-950 border border-zinc-700 text-zinc-100 text-center font-mono text-xs rounded"
                    value={formatUnit(bleed, appUnit)}
                    onChange={(e) => handleUpdate('bleed', parseUnit(e.target.value, appUnit))}
                  />
                  <button
                    onClick={() => handleNudge('bleed', 0.5)}
                    className="p-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded text-xs cursor-pointer"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                  <div className="flex gap-1">
                    {[0, 1.5, 2, 3].map(b => (
                      <button
                        key={b}
                        onClick={() => handleUpdate('bleed', parseUnit(b, appUnit))}
                        className="px-2 py-1 bg-zinc-950 hover:bg-zinc-800 text-[10px] text-zinc-300 rounded border border-zinc-800 font-mono"
                      >
                        {b}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* TAB 3: Real-Time Metrics & Geometry */}
          {activeTab === 'metrics' && (
            <motion.div 
              key="metrics"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={macSpring}
              className="space-y-2 text-xs font-mono"
            >
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 bg-zinc-900 border border-zinc-800 rounded-lg">
                  <div className="text-[10px] text-zinc-500 uppercase font-sans font-bold">Paper Efficiency</div>
                  <div className="text-sm font-bold text-emerald-400 mt-0.5">{efficiency}%</div>
                  <div className="w-full h-1.5 bg-zinc-800 rounded-full mt-1.5 overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${efficiency}%` }}></div>
                  </div>
                </div>

                <div className="p-2 bg-zinc-900 border border-zinc-800 rounded-lg">
                  <div className="text-[10px] text-zinc-500 uppercase font-sans font-bold">Fit Status</div>
                  <div className={`text-sm font-bold mt-0.5 ${isOverflow ? 'text-rose-400' : 'text-blue-400'}`}>
                    {isOverflow ? 'Over Bounds' : 'Fits 100%'}
                  </div>
                  <div className="text-[10px] text-zinc-400 font-sans mt-1">
                    {cols * rows} Stickers / Sheet
                  </div>
                </div>
              </div>

              <div className="p-2 bg-zinc-900/90 border border-zinc-800 rounded-lg space-y-1 text-[11px] text-zinc-300">
                <div className="flex justify-between">
                  <span className="text-zinc-400 font-sans">Sheet Size:</span>
                  <span>{formatUnit(sheetWidth, appUnit)} × {formatUnit(sheetHeight, appUnit)} {appUnit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400 font-sans">Printable Envelope:</span>
                  <span>{formatUnit(Math.max(0, sheetWidth - marginX * 2), appUnit)} × {formatUnit(Math.max(0, sheetHeight - marginY * 2), appUnit)} {appUnit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400 font-sans">Grid Occupied:</span>
                  <span className={isOverflow ? 'text-rose-400 font-bold' : 'text-zinc-200'}>
                    {formatUnit(totalOccupiedW, appUnit)} × {formatUnit(totalOccupiedH, appUnit)} {appUnit}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400 font-sans">Item Trim Size:</span>
                  <span>{formatUnit(itemW, appUnit)} × {formatUnit(itemH, appUnit)} {appUnit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400 font-sans">With Bleed (+{formatUnit(bleed, appUnit)}):</span>
                  <span>{formatUnit(itemWithBleedW, appUnit)} × {formatUnit(itemWithBleedH, appUnit)} {appUnit}</span>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
    </div>
  );
};
